// src/App.js
import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router';
import './App.css';
import Admin from './Admin';
import Explainer from './Explainer';
import VotingComponent from './VotingComponent';
import CreateGroup from './CreateGroup';
import UserGroups from './UserGroups';
import Map from './ble/Map';
import CompassMap from './compass/CompassMap';
import CompassEvents from './compass/CompassEvents';
import { app } from './firebaseConfig';
import { BM_DISCLAIMER } from './legal';
import { getAuth, signInAnonymously, onAuthStateChanged, signOut, setPersistence, browserLocalPersistence } from 'firebase/auth';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfUse from './TermsOfUse';
import Feedback from './Feedback';
import MySchedule from './MySchedule';
import StreakComponent from './StreakComponent';
import Header from './Header';
import InstallBanner, { APP_STORE_ID } from './InstallBanner';
import StateCharacteristic from './ble/StateCharacteristic';
import Events from './ble/Events';
import DeviceMenu from './ble/DeviceMenu';
import ProtobufDevice from './ble/protobufService/protobufDevice';
import Compass from './compass/compass';
import { ProtobufDeviceProvider } from './ble/protobufService/ProtobufDeviceContext';
import InlineErrorBoundary from './InlineErrorBoundary';
import { updateAnonVotes as v2UpdateAnonVotes, fetchTaxonomy, fetchConfig, setDefaultContextId, STORAGE_KEYS } from './v2Api';
import SignInWallModal from './SignInWallModal';

const auth = getAuth(app);
setPersistence(auth, browserLocalPersistence).catch(() => {});

// WEBUX-1: keep the Apple Smart App Banner's app-argument pointing at the CURRENT page, so iOS
// Safari's "Open" deep-links to the event/group being viewed rather than the site root. Must live
// inside <Router> — useLocation() throws outside one. Renders nothing.
function SmartBannerUpdater() {
  const location = useLocation();
  useEffect(() => {
    const meta = document.querySelector('meta[name="apple-itunes-app"]');
    if (meta) {
      meta.content = `app-id=${APP_STORE_ID}, app-argument=${window.location.href}`;
    }
  }, [location]);
  return null;
}

function App() {

  const [user, setUser] = useState(null);
  // anonToken is stored in localStorage with a 24 h TTL so it survives tab close
  // and can be merged on the next sign-in from a new tab (AUD-CR2-6).
  // sessionStorage was tab-scoped: closing the voting tab orphaned all anon votes.
  const [anonToken, setAnonToken] = useState(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEYS.anonToken);
      if (!raw) return null;
      const { token, expiresAt } = JSON.parse(raw);
      if (Date.now() > expiresAt) {
        window.localStorage.removeItem(STORAGE_KEYS.anonToken);
        return null;
      }
      return token;
    } catch { return null; }
  });
  const [showSignInWall, setShowSignInWall] = useState(false);
  const [showManualSignIn, setShowManualSignIn] = useState(false);
  const [wallVoteTotal, setWallVoteTotal] = useState(0);
  const [wallVotePositive, setWallVotePositive] = useState(0);
  const [appConfig, setAppConfig] = useState(null);
  // signInWallCfg is populated from fetched config only — no hardcoded fallback.
  // The sign-in wall must not fire before config is loaded (ARC-9).
  const signInWallCfg = useRef(null);
  const [showBrowserModal, setShowBrowserModal] = useState(false);
  const [appName] = useState("");

  const signInAnon = async () => {
    try {
      const anonUserCredential = await signInAnonymously(auth);
      const anonToken = await anonUserCredential.user.getIdToken();
      setAnonToken(anonToken);
      const expiresAt = Date.now() + 24 * 60 * 60 * 1000; // 24 h TTL
      window.localStorage.setItem(STORAGE_KEYS.anonToken, JSON.stringify({ token: anonToken, expiresAt }));
      console.log('Signed in anonymously');
    } catch (error) {
      console.error('Error signing in anonymously:', error);
    }
  };

  useEffect(() => {
    const authStateChanged = async (user) => {
      if (user) {
        setUser(user);
        const signedInToken = await user.getIdToken();
        if (anonToken && !user.isAnonymous) {
          await updateVotes(anonToken, signedInToken);
          window.localStorage.removeItem(STORAGE_KEYS.anonToken);
        }
      } else {
        console.log('No user is signed in');
        signInAnon();
      }
    };
    const unsubscribe = onAuthStateChanged(auth, authStateChanged);
    return () => unsubscribe();
  }, [anonToken]);

  // Called by VotingComponent after each anonymous vote. Tracks counters and
  // auto-triggers the sign-in wall when the config threshold is crossed (SW8, ARC-9).
  const handleAnonymousVote = (isPositive) => {
    const total = parseInt(localStorage.getItem(STORAGE_KEYS.signInWallTotal) || '0', 10) + 1;
    const positive = parseInt(localStorage.getItem(STORAGE_KEYS.signInWallPositive) || '0', 10) + (isPositive ? 1 : 0);
    localStorage.setItem(STORAGE_KEYS.signInWallTotal, total);
    localStorage.setItem(STORAGE_KEYS.signInWallPositive, positive);
    if (user?.isAnonymous && signInWallCfg.current) {
      const cfg = signInWallCfg.current;
      const lastPrompt = parseInt(localStorage.getItem(STORAGE_KEYS.signInWallLastPrompt) || '0', 10);
      const atFirst = total >= cfg.firstAtVotes && positive >= cfg.minPositive && !lastPrompt;
      const atRepeat = lastPrompt && (total - lastPrompt) >= cfg.repeatEveryVotes;
      if (atFirst || atRepeat) {
        localStorage.setItem(STORAGE_KEYS.signInWallLastPrompt, total);
        setWallVoteTotal(total);
        setWallVotePositive(positive);
        setShowSignInWall(true);
      }
    }
  };

  // Opens the sign-in wall on demand (called from StreakComponent's sign-in button tap).
  const handleOpenSignInWall = () => {
    const total = parseInt(localStorage.getItem(STORAGE_KEYS.signInWallTotal) || '0', 10);
    const positive = parseInt(localStorage.getItem(STORAGE_KEYS.signInWallPositive) || '0', 10);
    setWallVoteTotal(total);
    setWallVotePositive(positive);
    setShowSignInWall(true);
  };

  const handleWallDismiss = () => {
    setShowSignInWall(false);
  };

  const handleWallSignedIn = (signedInUser) => {
    setUser(signedInUser);
    setShowSignInWall(false);
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      localStorage.clear();
      setUser(null);
      console.log('User signed out');
      window.location.href = '/'; // Redirect to home page after sign out
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const updateVotes = async (anonToken, signedInToken) => {
    try {
      await v2UpdateAnonVotes({ anonToken, signedInToken });
    } catch (e) {
      console.error('v2 anon vote migration failed:', e);
    }
  };

  useEffect(() => {
    // WEB-EVENTCOUNT-1: deliberately NOT Promise.all. These two fetches have
    // nothing to do with each other, and an all-or-nothing await meant a
    // taxonomy failure also threw away the config — so the landing page would
    // sit on its placeholder event count because an endpoint the landing page
    // never touches was down.
    fetchTaxonomy()
      .then(taxonomy => localStorage.setItem(STORAGE_KEYS.v2Taxonomy, JSON.stringify(taxonomy)))
      .catch(e => console.error('taxonomy fetch failed:', e));

    fetchConfig()
      .then(cfgEnvelope => {
        const cfg = cfgEnvelope?.config || {};
        if (cfg.signInWall) signInWallCfg.current = cfg.signInWall;
        if (cfg.mode?.defaultContextId) setDefaultContextId(cfg.mode.defaultContextId);
        setAppConfig(cfg);
      })
      .catch(e => console.error('config fetch failed:', e));
  }, []); // once on mount

  return (
    <InlineErrorBoundary>
      <Router>
        <div className="App">
          <SmartBannerUpdater />
          <InstallBanner />
          <Header user={user} handleSignOut={handleSignOut} onSignIn={(!user || user.isAnonymous) ? () => setShowManualSignIn(true) : undefined} />
        {showSignInWall && (
          <SignInWallModal
            auth={auth}
            voteTotal={wallVoteTotal}
            votePositive={wallVotePositive}
            onSignedIn={handleWallSignedIn}
            onDismiss={handleWallDismiss}
            resendCooldownSecs={appConfig?.otp?.resendCooldownSecs}
          />
        )}
        {showManualSignIn && !showSignInWall && (
          <SignInWallModal
            auth={auth}
            reason="manual"
            onSignedIn={(signedInUser) => { setUser(signedInUser); setShowManualSignIn(false); }}
            onDismiss={() => setShowManualSignIn(false)}
            resendCooldownSecs={appConfig?.otp?.resendCooldownSecs}
          />
        )}
        {showBrowserModal && (
          <div className="modal">
            <div className="modal-content">
              <h2>Open in System Browser</h2>
              <p>It is easier to save your progress if you use Naviguesser in the system browser.</p>
              <a href={window.location.href} target="_blank" rel="noopener noreferrer" className="btn active-button">Open in system browser</a><br/>
              <button onClick={() => setShowBrowserModal(false)} className="secondary-button">Keep using inside {appName}</button>
            </div>
          </div>
        )}
        <Routes>
          <Route path="/admin" element={<Admin user={user} />} />
          <Route path="/options" element={<VotingComponent user={user} onAnonymousVote={handleAnonymousVote} embargoConfig={appConfig?.mode} streakConfig={appConfig?.streak} />} />
          <Route path="/options/:event_id" element={<VotingComponent user={user} onAnonymousVote={handleAnonymousVote} embargoConfig={appConfig?.mode} streakConfig={appConfig?.streak} />} />
          <Route path="/streak" element={<StreakComponent user={user} onSignInTapped={handleOpenSignInWall} streakConfig={appConfig?.streak} />} />
          <Route path="/new_group" element={<CreateGroup user={user} />} />
          <Route path="/" element={
            <>
              <UserGroups user={user} />
              <Explainer user={user} eventsCount={appConfig?.display?.eventsCount} />
            </>
          } />
          <Route path="/groups/:link_key" element={
            <>
              <UserGroups user={user} />
              <Explainer user={user} eventsCount={appConfig?.display?.eventsCount} />
            </>
          } />
          <Route path="/picks" element={<MySchedule user={user} embargoConfig={appConfig?.mode} />} />
          {/* Legacy path kept for old bookmarks/shared links — redirect to /picks. */}
          <Route path="/schedule" element={<Navigate to="/picks" replace />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-use" element={<TermsOfUse />} />
          <Route path="/feedback" element={<Feedback />} />
          <Route path="/device/*" element={
            <ProtobufDeviceProvider>
              <DeviceMenu />
              <Routes>
                <Route path="*" element={<Navigate to="/device/" />} />
                <Route path="/" element={<Compass />} />
                <Route path="/old" element={<StateCharacteristic />} />
                <Route path="map" element={<Map user={user} />} />
                <Route path="compass-map" element={<CompassMap />} />
                <Route path="compass-events" element={<CompassEvents />} />
                <Route path="events" element={<Events user={user} />} />
                <Route path="protobuf" element={<ProtobufDevice />} />
              </Routes>
            </ProtobufDeviceProvider>
          } />
        </Routes>
        <footer>
          {/* The BM ToS wants the disclaimer on any page the app can be downloaded from. Since the
              install banner and App Store link shipped, that includes the landing page — so it goes
              in the footer, which renders on every route, rather than only on the legal pages. */}
          <small className="bm-disclaimer">{BM_DISCLAIMER}</small>
          <small>
            <a href="https://www.instagram.com/lightdutymagic/" target='_blank' rel="noreferrer">© 2023–2026 Evgeny Balashov</a> - <a href="/terms-of-use">Terms of Use</a> - <a href="/privacy-policy">Privacy Policy</a> - <a href="/feedback">Feedback</a>
          </small>
        </footer>
      </div>
    </Router>
    </InlineErrorBoundary>
  );
}

export default App;
